/**
 * Wiederverwendbarer Button für PDF-Downloads.
 * Ersetzt das duplizierte Inline-Pattern in AdminUploads, AdminBewerbungen,
 * TeamPortal und Admin (SEPA-Tab).
 *
 * Beispiel:
 *   <PdfDownloadButton
 *     endpoint={`/admin/anfragen/${a.anfrage_id}/pdf`}
 *     filename={`Anfrage_${a.name}_${a.anfrage_id}.pdf`}
 *     label="PDF herunterladen"
 *     variant="icon"
 *   />
 */
import React, { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { api } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const sanitizeFilename = (name) => {
  if (!name) return 'export.pdf';
  let safe = String(name).replace(/[^\w.\-\s]+/g, '_').replace(/\s+/g, '_');
  if (!safe.toLowerCase().endsWith('.pdf')) safe += '.pdf';
  return safe;
};

export const PdfDownloadButton = ({
  endpoint,
  filename,
  label = 'PDF',
  variant = 'button',    // 'button' | 'icon' | 'link'
  className = '',
  title,
  onSuccess,
  testid,
  size = 'sm',           // 'sm' | 'md'
}) => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleClick = async (e) => {
    e?.stopPropagation?.();
    if (loading) return;
    setLoading(true);
    try {
      const resp = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', sanitizeFilename(filename));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'PDF konnte nicht erzeugt werden.';
      toast({ title: 'PDF-Export fehlgeschlagen', description: detail, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (variant === 'icon') {
    const dim = size === 'md' ? 'w-9 h-9' : 'w-8 h-8';
    const iconSz = size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        data-testid={testid}
        title={title || label}
        className={`${dim} flex items-center justify-center rounded-sm text-[#1B3A5C] hover:bg-[#fbf7f0] hover:text-[#b8893d] transition-colors disabled:opacity-50 ${className}`}
      >
        {loading
          ? <Loader2 className={`${iconSz} animate-spin`} />
          : <Download className={iconSz} />}
      </button>
    );
  }

  if (variant === 'link') {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        data-testid={testid}
        className={`inline-flex items-center gap-1.5 text-xs text-[#b8893d] hover:text-[#a07730] hover:underline disabled:opacity-50 ${className}`}
      >
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
        {label}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      data-testid={testid}
      title={title}
      className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs tracking-wider border border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white rounded-sm transition-colors disabled:opacity-50 ${className}`}
    >
      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
};

export default PdfDownloadButton;
